import {
  buildAxiosAuthenticationHeaders,
  buildAxiosAuthenticationHeadersForMultipart,
} from "@src/cloud-connect/api/util/build-axios-authentication-headers";
import { parseMaybeJson } from "@src/cloud-connect/api/util/parse-maybe-json";
import { createLinkedAbortController } from "@src/cloud-connect/api/util/create-linked-abort-controller";
import type { StreamRequestOptions } from "@src/cloud-connect/api/util/create-linked-abort-controller";
import { parseSupportTicketSseStreamMessage } from "@src/cloud-connect/api/util/parse-support-ticket-sse-stream";
import { supportUrlEnvVar } from "@src/cloud-connect/feature-toggles/feature-toggles";
import {
  PostSupportCommentByTicketPayload,
  TicketCommentAttachment,
} from "@src/types/support";

export type CommentStreamEvent =
  | { type: "ready" }
  | {
      type: "meta";
      userMessageId?: string;
      assistantMessageId?: string;
    }
  | { type: "files"; attachments: TicketCommentAttachment[] }
  | { type: "token"; text: string }
  | { type: "tool-call"; data: unknown }
  | { type: "done" }
  | { type: "error"; message: string };

/**
 * Open a POST to the comment endpoint using the Fetch API. Parses SSE events as
 * they arrive and yields typed events.
 *
 * The endpoint streams only when the ticket is handled by the AI chatbot; for
 * every other ticket it answers 201 JSON and no tokens follow. The user's
 * comment is created first, then the AI response tokens stream back.
 */
export async function* postSupportCommentWithAIStream(
  namespace: string,
  ticketId: number | string,
  payload: PostSupportCommentByTicketPayload,
  options?: StreamRequestOptions,
  files?: File[],
): AsyncGenerator<CommentStreamEvent, void, void> {
  const url = supportUrlEnvVar();

  const imageFiles = files ?? [];
  const hasFiles = imageFiles.length > 0;
  // v4.0.0 service-support: AI stream under /api/v2; multipart variant for
  // image uploads. Non-AI-handled tickets answer 201 JSON with no stream.
  const endpoint = hasFiles
    ? `tickets/${ticketId}/comments/ai-with-images`
    : `tickets/${ticketId}/comments/ai`;
  const fullUrl = `${url}/api/v2/ns/${namespace}/${endpoint}`;

  const abortController = createLinkedAbortController(options?.signal);
  // Set once a "done" or "error" SSE event is actually seen
  let isAPIResponseCompleted = false;

  try {
    let body: string | FormData;
    let requestHeaders: Record<string, string>;
    if (hasFiles) {
      // FormData bodies must set their own multipart Content-Type — never send
      // a JSON content-type with them.
      requestHeaders = {
        Accept: "text/event-stream",
        ...buildAxiosAuthenticationHeadersForMultipart(),
      };
      const formData = new FormData();
      formData.append("comment", JSON.stringify(payload));
      for (const file of imageFiles) {
        formData.append("files", file);
      }
      body = formData;
    } else {
      body = JSON.stringify(payload);
      requestHeaders = {
        "Content-Type": "application/json",
        Accept: "text/event-stream",
        ...buildAxiosAuthenticationHeaders(),
      };
    }

    const response = await fetch(fullUrl, {
      method: "POST",
      headers: requestHeaders,
      body,
      signal: abortController.signal,
    });

    if (!response.ok) {
      let errorDetail = "Failed to add comment.";
      try {
        const body = await response.json();
        errorDetail = body.detail || body.message || errorDetail;
      } catch {
        // keep default error message
      }
      yield { type: "error", message: errorDetail };
      return;
    }

    const contentType = response.headers.get("content-type") ?? "";
    if (!contentType.includes("text/event-stream")) {
      // 201 JSON - comment posted, no AI stream
      yield { type: "ready" };
      return;
    }

    const reader = response.body?.getReader();
    if (!reader) {
      yield { type: "error", message: "Failed to read response stream." };
      return;
    }

    // POST succeeded - consumer can show the user comment before tokens arrive
    yield { type: "ready" };

    for await (const { event, data } of parseSupportTicketSseStreamMessage(
      reader,
    )) {
      if (event === "meta") {
        const parsed = parseMaybeJson(data) as {
          user_message_id?: string;
          assistant_message_id?: string;
        };
        yield {
          type: "meta",
          userMessageId: parsed?.user_message_id,
          assistantMessageId: parsed?.assistant_message_id,
        };
      } else if (event === "files") {
        const parsed = parseMaybeJson(data) as {
          attachments?: TicketCommentAttachment[];
        };
        yield { type: "files", attachments: parsed?.attachments ?? [] };
      } else if (event === "token") {
        yield { type: "token", text: data };
      } else if (event === "tool-call" || event === "tool_call") {
        yield { type: "tool-call", data: parseMaybeJson(data) };
      } else if (event === "done") {
        isAPIResponseCompleted = true;
        yield { type: "done" };
        return;
      } else if (event === "error") {
        isAPIResponseCompleted = true;
        yield { type: "error", message: data };
        return;
      }
      // heartbeat - ignored
    }
  } catch (err) {
    if (options?.signal?.aborted) {
      return;
    }
    console.error("postSupportCommentWithAIStream stream read failed:", err);
  } finally {
    abortController.abort();
  }

  // Reached whenever the stream ends (cleanly or via a caught read error) without a
  // "done"/"error" SSE event — e.g. a proxy timeout, a dropped connection, or a chunked
  // response that terminates abnormally.
  if (!isAPIResponseCompleted && !options?.signal?.aborted) {
    yield {
      type: "error",
      message: "The connection closed before the response completed.",
    };
  }
}
